// Weapon settings page: General / Defaults / one tab per weapon.
//
// Editing a weapon tab only writes *overrides*; inherited values are shown
// disabled with an "override" checkbox. The Defaults tab edits the shared
// profile directly.

import { WEAPON_ICONS } from "../rules";
import {
  ALT_ZOOM_ONLY,
  DEFAULT_POSITION,
  ZOOM_WEAPONS,
  clampProfileNumber,
  linkedZoomSensitivity,
  normalizeWeaponSettings,
  resolveWeapon,
  type GlobalDefaults,
  type WeaponPositionConfig,
  type WeaponProfile,
  type WeaponSettings,
} from "../weapons/settings";
import { WEAPON_ICON_KEYS, WEAPON_IDS, weaponName } from "../weapons/names";
import { dsElement, refreshComponentStyles } from "./daisy";
import { SensPicker } from "./sens_picker";

export interface WeaponSettingsCallbacks {
  onChange: (settings: WeaponSettings) => void;
  onEditCrosshair: (slotId: string) => void;
  /** Defaults-tab picker: write the global sensitivity. */
  onDefaultSensitivity: (sens: number) => void;
  /** Apply a FOV from the picker (global). */
  onFov: (fov: number) => void;
  /** Read the live debug flags (raw mouse lives on this tab). */
  getDebug?: () => Record<string, unknown>;
  /** Current raw-pointer status (raw granted / OS curve / unsupported). */
  getRawStatus?: () => string;
  /** Write a debug flag. */
  onDebug?: (key: string, value: boolean) => void;
}

interface SlotRef {
  id: string;
  name: string;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export class WeaponSettingsPanel {
  private ws: WeaponSettings;
  private globals: GlobalDefaults;
  private slots: SlotRef[];
  private tab: number | "general" | "defaults" = "defaults";
  private root: HTMLElement;
  private body!: HTMLElement;
  private tabsEl!: HTMLElement;
  private sensPicker: SensPicker;

  constructor(
    host: HTMLElement,
    ws: WeaponSettings,
    globals: GlobalDefaults,
    slots: SlotRef[],
    private cb: WeaponSettingsCallbacks,
  ) {
    this.ws = normalizeWeaponSettings(ws);
    this.globals = globals;
    this.slots = slots;
    this.root = host;
    this.sensPicker = new SensPicker({
      onApply: (scope, sens) => {
        if (scope === "defaults") {
          this.globals.sensitivity = sens;
          this.cb.onDefaultSensitivity(sens);
          return;
        }
        const key = String(scope);
        this.ws.enabled = true;
        this.ws.overrides[key] = { ...(this.ws.overrides[key] ?? {}), sensitivity: sens };
        this.changed();
      },
      onFov: (fov) => this.cb.onFov(fov),
      onPin: (weapon, sens) => {
        const key = String(weapon);
        const own = this.ws.overrides[key];
        if (sens === null) {
          if (own) {
            delete own.sensitivity;
            if (Object.keys(own).length === 0) delete this.ws.overrides[key];
          }
        } else {
          this.ws.enabled = true;
          this.ws.overrides[key] = { ...(own ?? {}), sensitivity: sens };
        }
        this.changed();
      },
    });
    this.build();
  }

  set(ws: WeaponSettings, globals: GlobalDefaults, slots: SlotRef[]): void {
    this.ws = normalizeWeaponSettings(ws);
    this.globals = globals;
    this.slots = slots;
    this.render();
  }

  /**
   * Update inherited values without rebuilding the panel. Used for live slider
   * edits (sensitivity/FOV), where a re-render would destroy the dragged
   * slider and steal focus.
   */
  setGlobals(globals: GlobalDefaults): void {
    Object.assign(this.globals, globals);
  }

  activate(): void {
    // Keep the last selected tab (Defaults / General / a specific weapon)
    // across menu visits. The constructor starts on Defaults, so the first
    // entry still lands there; after that the panel remembers what you were
    // editing (weapon changes are easy to lose otherwise).
    this.render();
  }

  /** Jump to a tab (used by the settings search). */
  showTab(tab: number | "general" | "defaults"): void {
    this.tab = tab;
    this.render();
  }

  private build(): void {
    this.root.innerHTML = "";
    this.tabsEl = el("div", "weapon-tabs");
    this.body = el("div", "weapon-cards");
    this.root.append(this.tabsEl, this.body);
    this.render();
  }

  private changed(): void {
    this.cb.onChange(clone(this.ws));
  }

  /**
   * One weapon-settings category as a design-system card (`data-ds="card"`),
   * matching the Settings page cards. `data-ds-keep` preserves the layout
   * classes when the theme pass rewrites the class list.
   */
  private card(title: string): { card: HTMLElement; body: HTMLElement } {
    const card = el("section", "settings-card settings-card--wide");
    card.dataset.ds = "card";
    card.dataset.dsKeep = "settings-card settings-card--wide";
    const body = el("div", "card-body");
    body.append(el("h3", "card-title", title));
    card.append(body);
    return { card, body };
  }

  private render(): void {
    this.tabsEl.innerHTML = "";
    const mk = (label: string, key: number | "general" | "defaults", icon?: string) => {
      const b = el("button", "weapon-tab");
      if (icon) {
        b.innerHTML = `<svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round" stroke-linecap="round"><path d="${icon}"/></svg>`;
      } else {
        b.textContent = label;
      }
      b.title = label;
      b.classList.toggle("active", this.tab === key);
      b.addEventListener("click", () => {
        this.tab = key;
        this.render();
      });
      this.tabsEl.append(b);
    };
    mk("General", "general");
    mk("Defaults", "defaults");
    for (const id of WEAPON_IDS) {
      mk(weaponName(id), id, WEAPON_ICONS[WEAPON_ICON_KEYS[id]]);
    }

    this.body.innerHTML = "";
    if (this.tab === "general") this.renderGeneral();
    else if (this.tab === "defaults") this.renderProfile("defaults");
    else this.renderProfile(this.tab);
    // Cards are built after the theme pass, so re-run the class pass.
    refreshComponentStyles();
  }

  private renderGeneral(): void {
    const general = this.card("Weapon settings");
    const row = el("div", "profile-row");
    row.append(el("label", "", "Enable per-weapon settings"));
    const combo = el("div", "combo");
    const cb = el("input");
    cb.type = "checkbox";
    cb.checked = this.ws.enabled;
    dsElement(cb, "toggle", { color: "primary", size: "sm" });
    cb.addEventListener("change", () => {
      this.ws.enabled = cb.checked;
      this.changed();
      this.render();
    });
    combo.append(cb);
    row.append(combo, el("span", "", ""));
    general.body.append(row);

    const hint = el("div", "weapon-note");
    hint.textContent =
      "Each weapon tab overrides only the fields you enable. With this off, feel settings use the global Controls/Video values and effects use the shipped per-weapon baseline. Rocket secondary fire runs the built-in rocket-dash script: back + down/crouch + dash + strong fire.";
    general.body.append(hint);

    const zoom = this.card("Zoom");
    const z = el("div", "weapon-note");
    z.textContent = `Zoom-capable weapons: ${[...ZOOM_WEAPONS].map(weaponName).join(", ")}. Rail and Lightning Gun have no weak fire: Mouse 4 / alt-fire is always zoom there.`;
    zoom.body.append(z);

    const fov = this.card("FOV converter");
    fov.body.append(this.fovConverter());

    const reset = el("button", "xhair-btn", "Reset all weapon overrides");
    dsElement(reset, "button", { style: "outline", size: "sm" });
    reset.addEventListener("click", () => {
      this.ws.overrides = {};
      this.ws.defaults = {};
      this.changed();
      this.render();
    });
    fov.body.append(reset);
    this.body.append(general.card, zoom.card, fov.card);
  }

  private fovConverter(): HTMLElement {
    const wrap = el("div");
    const hRow = el("div", "profile-row");
    hRow.append(el("label", "", "Quake FOV (horizontal)"));
    const combo = el("div", "combo");
    const h = el("input");
    h.type = "number";
    h.min = "60";
    h.max = "160";
    h.value = String(this.globals.fov);
    dsElement(h, "input", { size: "sm", keep: "w-20" });
    const aspectSel = el("select");
    for (const [label, value] of [
      ["16:9", 16 / 9],
      ["16:10", 16 / 10],
      ["4:3", 4 / 3],
    ] as [string, number][]) {
      const o = el("option", "", label);
      o.value = String(value);
      aspectSel.append(o);
    }
    aspectSel.value = String(16 / 9);
    const out = el("div", "weapon-note");
    const update = () => {
      const hfov = Math.min(160, Math.max(60, Number(h.value) || 125));
      const aspect = Number(aspectSel.value) || 16 / 9;
      const vfov = (2 * Math.atan(Math.tan((hfov * Math.PI) / 360) / aspect) * 180) / Math.PI;
      const h43 = (2 * Math.atan((Math.tan((hfov * Math.PI) / 360) * 4) / 3 / aspect) * 180) / Math.PI;
      out.textContent = `Vertical ${vfov.toFixed(2)}° · 4:3 horizontal ${Math.min(179, h43).toFixed(2)}°`;
    };
    h.addEventListener("input", update);
    aspectSel.addEventListener("change", update);
    combo.append(h, aspectSel);
    hRow.append(combo, el("span", "", ""));
    wrap.append(hRow, out);
    update();
    return wrap;
  }

  /** `scope` is `defaults` or a weapon id. */
  private renderProfile(scope: "defaults" | number): void {
    // The Defaults picker owns the *global* sensitivity, so it must stay usable
    // even when per-weapon overrides are switched off.
    const pickerOnly = scope === "defaults" && !this.ws.enabled;
    if (!this.ws.enabled && scope !== "defaults") {
      this.body.append(el("div", "weapon-note", "Enable per-weapon settings on the General tab first."));
      return;
    }
    const weapon = scope === "defaults" ? null : scope;
    const altZoomOnly = weapon !== null && ALT_ZOOM_ONLY.has(weapon);
    const own = scope === "defaults" ? this.ws.defaults : (this.ws.overrides[String(scope)] ??= {});
    const inherited = scope !== "defaults";
    const fallback = resolveWeapon(normalizeWeaponSettings({ ...this.ws, overrides: {} }), this.globals, weapon);
    const resolved = resolveWeapon(this.ws, this.globals, weapon);

    const head = this.card(scope === "defaults" ? "Shared defaults" : weaponName(scope));
    head.body.append(
      el(
        "div",
        "weapon-note",
        pickerOnly
          ? "Per-weapon settings are off. The picker below still sets your global sensitivity; enable per-weapon settings on General to pin individual weapons."
          : scope === "defaults"
            ? "Every weapon inherits these values unless it overrides them."
            : "Only enabled rows override the shared defaults for this weapon.",
      ),
    );
    if (pickerOnly) {
      this.body.append(head.card);
      const only = this.card("Sensitivity");
      this.sensPicker.render(only.body, scope, this.ws, this.globals);
      this.body.append(only.card);
      return;
    }

    const sens = this.card("Sensitivity");
    this.sensPicker.render(sens.body, scope, this.ws, this.globals);
    sens.body.append(
      (() => {
        // Long explanation lives on hover instead of taking a line of the card.
        const note = el(
          "div",
          "weapon-note weapon-note-hover",
          scope === "defaults" ? "how weapon overrides work" : "what this button does",
        );
        note.title =
          scope === "defaults"
            ? "The default applies to every weapon without an override; the pills pin individual weapons."
            : "Use for this weapon stores a per-weapon override (the Defaults tab shows and pins all of them).";
        return note;
      })(),
      this.numRow(own, "yawMultiplier", "Yaw multiplier", 0.05, 10, 0.01, fallback.yawMultiplier, inherited),
      this.numRow(own, "pitchMultiplier", "Pitch multiplier", 0.05, 10, 0.01, fallback.pitchMultiplier, inherited),
      this.selectRow(
        own,
        "zoomSensMode",
        "Zoomed sensitivity",
        [
          { value: "linked", label: "Linked to FOV (same screen distance)" },
          { value: "absolute", label: "Absolute" },
        ],
        fallback.zoomSensMode,
        inherited,
      ),
      this.numRow(
        own,
        "zoomSensitivity",
        "Zoomed value",
        0.01,
        50,
        0.001,
        fallback.zoomSensitivity,
        inherited,
        resolved.zoomSensMode === "linked",
      ),
    );
    const derived = el("div", "weapon-note");
    derived.textContent = `Linked value: ${linkedZoomSensitivity(resolved.sensitivity, resolved.fov, resolved.zoomFov).toFixed(6)}`;
    sens.body.append(derived);

    const fov = this.card("Field of view");
    fov.body.append(
      this.numRow(own, "fov", "Normal FOV", 60, 160, 1, fallback.fov, inherited),
      this.numRow(own, "zoomFov", "Zoom FOV", 10, 160, 1, fallback.zoomFov, inherited),
      this.boolRow(own, "zoomToggle", "Toggle zoom", fallback.zoomToggle, inherited),
      this.numRow(own, "zoomTime", "Zoom time (ms)", 0, 500, 10, fallback.zoomTime, inherited),
    );

    const ch = this.card("Crosshair");
    ch.body.append(this.slotRow(own, "crosshair", "Crosshair", inherited), this.slotRow(own, "zoomCrosshair", "Zoom crosshair", inherited));

    const mouse = this.card("Mouse");
    // Raw input lives with the other mouse settings. The panel rebuilds its
    // DOM on every render, so the control is created here rather than moved
    // from static markup.
    if (this.cb.onDebug && this.cb.getDebug) {
      const rawRow = el("div", "rule-toggles");
      rawRow.append(el("span", "rule-toggle-label", "Raw input"));
      const raw = document.createElement("input");
      raw.type = "checkbox";
      raw.id = "raw-input-toggle";
      raw.dataset.ds = "toggle";
      raw.dataset.dsColor = "primary";
      raw.dataset.dsSize = "sm";
      raw.title =
        "Request raw (unadjusted) pointer input. Off = let the OS/driver apply its curve.";
      raw.checked = Boolean(this.cb.getDebug()["rawmouse"]);
      raw.addEventListener("change", () => this.cb.onDebug?.("rawmouse", raw.checked));
      rawRow.append(raw);
      const status = this.cb.getRawStatus?.() ?? "";
      if (status) rawRow.append(el("span", "val", status));
      mouse.body.append(rawRow);
      if (/unsupported/i.test(status)) {
        mouse.body.append(
          el(
            "div",
            "weapon-note",
            "Linux Chromium refuses unadjusted movement, so the OS pointer curve stays " +
              "active. Turn it off at the OS level: KDE → Input Devices → Mouse → " +
              "Acceleration profile: None, or GNOME → " +
              "`gsettings set org.gnome.desktop.peripherals.mouse accel-profile flat`. " +
              "Windows/macOS Chromium grants raw input directly.",
          ),
        );
      }
    }
    mouse.body.append(
      this.selectRow(
        own,
        "mouse4",
        "Mouse 4",
        [
          { value: "secondary", label: "Secondary fire" },
          { value: "zoom", label: "Zoom" },
        ],
        fallback.mouse4,
        inherited,
        altZoomOnly,
      ),
    );
    if (altZoomOnly) {
      mouse.body.append(
        el("div", "weapon-note", "This weapon has no weak fire; Mouse 4 / alt-fire always zooms."),
      );
    }
    // Acceleration placeholder: the value round-trips through settings but is
    // deliberately not applied until its own focused session.
    const accelRow = el("div", "profile-row");
    accelRow.append(el("label", "", "Acceleration"));
    const accelCombo = el("div", "combo");
    const accelSel = el("select");
    for (const t of ["off", "linear", "power", "classic"]) {
      const o = el("option", "", t);
      o.value = t;
      accelSel.append(o);
    }
    const accelState = own.accel?.type ?? fallback.accel.type;
    accelSel.value = accelState;
    accelSel.addEventListener("change", () => {
      own.accel = { ...fallback.accel, ...own.accel, type: accelSel.value as "off" | "linear" | "power" | "classic" };
      this.changed();
    });
    accelCombo.append(accelSel);
    accelRow.append(accelCombo, el("span", "inherit", "placeholder"));
    mouse.body.append(accelRow);
    const accel = el("div", "weapon-note");
    accel.textContent =
      "Stored per weapon but not applied yet — acceleration gets its own focused session (curve, cap, exponent).";
    mouse.body.append(accel);

    const sounds = this.card("Sounds");
    sounds.body.append(this.boolRow(own, "hitSounds", "Hit sounds", fallback.hitSounds, inherited));

    const fx = this.card("Effects");
    fx.body.append(
      this.boolRow(own, "vfxSmoke", "Smoke", fallback.vfxSmoke, inherited),
      this.numRow(own, "vfxSmokeAmount", "Smoke amount", 0, 3, 0.1, fallback.vfxSmokeAmount, inherited),
      this.numRow(own, "vfxSmokeOpacity", "Smoke opacity", 0, 1, 0.05, fallback.vfxSmokeOpacity, inherited),
      this.numRow(own, "vfxSmokeSize", "Smoke size", 0.2, 3, 0.1, fallback.vfxSmokeSize, inherited),
      this.boolRow(own, "vfxParticles", "Particles", fallback.vfxParticles, inherited),
      this.numRow(own, "vfxParticleAmount", "Particle amount", 0, 3, 0.1, fallback.vfxParticleAmount, inherited),
      this.numRow(own, "vfxParticleSize", "Particle size", 0.2, 3, 0.1, fallback.vfxParticleSize, inherited),
      this.selectRow(
        own,
        "vfxTrailMode",
        "Projectile trail",
        [
          { value: "both", label: "Glow + smoke" },
          { value: "glow", label: "Glow only" },
          { value: "smoke", label: "Smoke only" },
          { value: "off", label: "Off" },
        ],
        fallback.vfxTrailMode,
        inherited,
      ),
      this.numRow(own, "vfxTrailMs", "Glow trail (ms)", 40, 2000, 10, fallback.vfxTrailMs, inherited),
      this.numRow(own, "vfxSmokeTrailMs", "Smoke trail (ms)", 40, 3000, 10, fallback.vfxSmokeTrailMs, inherited),
      this.numRow(own, "vfxBeamTrailMs", "Beam trail (ms)", 0, 2000, 10, fallback.vfxBeamTrailMs, inherited),
    );
    fx.body.append(
      el(
        "div",
        "weapon-note",
        "Client-side effects for this weapon. The Video tab's VFX amount/size/smoke sliders multiply these. Projectile colour and opacity live in Rules → this weapon. Glow/smoke trail = how long those puffs linger behind a projectile; beam trail only affects hitscan weapons.",
      ),
    );

    const pos = this.card("Weapon position");
    pos.body.append(this.positionRows(own, inherited, fallback.weaponPosition ?? DEFAULT_POSITION));
    pos.body.append(
      el(
        "div",
        "weapon-note",
        "X = forward · Y = right · Z = up (view units). Size scales the model. Beams and flashes start at the same muzzle.",
      ),
    );

    this.body.append(head.card, sens.card, fov.card, ch.card, mouse.card, sounds.card, fx.card, pos.card);
  }

  private rowShell(
    label: string,
    inherit: boolean,
    overridden: boolean,
  ): { row: HTMLElement; badge: HTMLElement; toggle: HTMLInputElement | null } {
    const row = el("div", "profile-row");
    row.append(el("label", "", label));
    const combo = el("div", "combo");
    row.append(combo);
    const badge = el("label", "inherit");
    let toggle: HTMLInputElement | null = null;
    if (inherit) {
      toggle = el("input");
      toggle.type = "checkbox";
      toggle.checked = overridden;
      toggle.title = "override for this weapon";
      dsElement(toggle, "checkbox", { color: "primary", size: "sm" });
      badge.append(toggle, el("span", "", "override"));
      badge.classList.toggle("overridden", overridden);
      row.classList.toggle("inherited", !overridden);
    }
    row.append(badge);
    return { row, badge, toggle };
  }

  private numRow(
    scope: WeaponProfile,
    key: keyof WeaponProfile,
    label: string,
    min: number,
    max: number,
    step: number,
    fallback: number,
    inherit: boolean,
    disabled = false,
  ): HTMLElement {
    const rec = scope as Record<string, unknown>;
    const has = typeof rec[key as string] === "number";
    const value = has ? (rec[key as string] as number) : fallback;
    const { row, badge, toggle } = this.rowShell(label, inherit, inherit ? has : true);
    const combo = row.querySelector(".combo") as HTMLElement;
    const input = el("input");
    input.type = "number";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.disabled = disabled || (inherit && !has);
    dsElement(input, "input", { size: "sm", keep: "w-20" });
    input.addEventListener("input", () => {
      if (input.disabled) return;
      const v = clampProfileNumber(key, Number(input.value));
      if (v === null) return;
      rec[key as string] = v;
      this.changed();
    });
    combo.append(input);
    badge.title = `inherited: ${fallback}`;
    if (toggle) {
      toggle.addEventListener("change", () => {
        if (toggle.checked) rec[key as string] = fallback;
        else delete rec[key as string];
        this.changed();
        this.render();
      });
    }
    return row;
  }

  private boolRow(
    scope: WeaponProfile,
    key: keyof WeaponProfile,
    label: string,
    fallback: boolean,
    inherit: boolean,
  ): HTMLElement {
    const rec = scope as Record<string, unknown>;
    const has = typeof rec[key as string] === "boolean";
    const value = has ? (rec[key as string] as boolean) : fallback;
    const { row, toggle } = this.rowShell(label, inherit, inherit ? has : true);
    const combo = row.querySelector(".combo") as HTMLElement;
    const input = el("input");
    input.type = "checkbox";
    input.checked = value;
    input.disabled = inherit && !has;
    dsElement(input, "toggle", { color: "primary", size: "sm" });
    input.addEventListener("change", () => {
      if (input.disabled) return;
      rec[key as string] = input.checked;
      this.changed();
    });
    combo.append(input);
    if (toggle) {
      toggle.addEventListener("change", () => {
        if (toggle.checked) rec[key as string] = fallback;
        else delete rec[key as string];
        this.changed();
        this.render();
      });
    }
    return row;
  }

  private selectRow(
    scope: WeaponProfile,
    key: keyof WeaponProfile,
    label: string,
    options: { value: string; label: string }[],
    fallback: string,
    inherit: boolean,
    disabled = false,
  ): HTMLElement {
    const rec = scope as Record<string, unknown>;
    const has = rec[key as string] !== undefined;
    const value = has ? String(rec[key as string]) : fallback;
    const { row, toggle } = this.rowShell(label, inherit, inherit ? has : true);
    const combo = row.querySelector(".combo") as HTMLElement;
    const sel = el("select");
    for (const o of options) {
      const opt = el("option", "", o.label);
      opt.value = o.value;
      sel.append(opt);
    }
    sel.value = value;
    sel.disabled = disabled || (inherit && !has);
    sel.addEventListener("change", () => {
      if (sel.disabled) return;
      rec[key as string] = sel.value;
      this.changed();
      this.render();
    });
    combo.append(sel);
    if (toggle) {
      toggle.addEventListener("change", () => {
        if (toggle.checked) rec[key as string] = fallback;
        else delete rec[key as string];
        this.changed();
        this.render();
      });
    }
    return row;
  }

  private slotRow(
    scope: WeaponProfile,
    key: "crosshair" | "zoomCrosshair",
    label: string,
    inherit: boolean,
  ): HTMLElement {
    const rec = scope as Record<string, unknown>;
    const has = typeof rec[key] === "string" && rec[key] !== "";
    const value = has ? String(rec[key]) : "";
    const { row, toggle } = this.rowShell(label, inherit, inherit ? has : true);
    const combo = row.querySelector(".combo") as HTMLElement;
    const sel = el("select");
    const none = el("option", "", key === "zoomCrosshair" ? "— same as normal —" : "— inherit —");
    none.value = "";
    sel.append(none);
    for (const s of this.slots) {
      const o = el("option", "", s.name);
      o.value = s.id;
      sel.append(o);
    }
    sel.value = value;
    sel.disabled = inherit && !has;
    const edit = el("button", "xhair-btn", "Edit");
    dsElement(edit, "button", { style: "outline", size: "sm" });
    edit.disabled = inherit && !has;
    edit.addEventListener("click", () => {
      const id = sel.value || this.slots.find((s) => s.id === "default")?.id || this.slots[0]?.id;
      if (id) this.cb.onEditCrosshair(id);
    });
    sel.addEventListener("change", () => {
      if (sel.value) rec[key] = sel.value;
      else delete rec[key];
      edit.disabled = !sel.value;
      this.changed();
    });
    combo.append(sel, edit);
    if (toggle) {
      toggle.addEventListener("change", () => {
        if (toggle.checked) rec[key] = value || this.slots[0]?.id || "default";
        else delete rec[key];
        this.changed();
        this.render();
      });
    }
    return row;
  }

  private positionRows(scope: WeaponProfile, inherit: boolean, fallback: WeaponPositionConfig): HTMLElement {
    const wrap = el("div");
    const current = () => scope.weaponPosition ?? fallback;
    if (inherit) {
      const ov = el("div", "profile-row");
      ov.append(el("label", "", "Override"));
      const combo = el("div", "combo");
      const box = el("input");
      box.type = "checkbox";
      box.checked = !!scope.weaponPosition;
      box.title = "use a custom weapon position for this weapon";
      dsElement(box, "checkbox", { color: "primary", size: "sm" });
      box.addEventListener("change", () => {
        if (box.checked) scope.weaponPosition = { ...fallback };
        else delete scope.weaponPosition;
        this.changed();
        this.render();
      });
      combo.append(box);
      ov.append(combo, el("span", "inherit", scope.weaponPosition ? "custom" : "inherited"));
      wrap.append(ov);
    }
    const preset = el("div", "profile-row");
    preset.append(el("label", "", "Preset"));
    const combo = el("div", "combo");
    const sel = el("select");
    for (const p of ["hidden", "right", "center", "left"]) {
      const o = el("option", "", p);
      o.value = p;
      sel.append(o);
    }
    sel.value = current().preset;
    sel.disabled = inherit && !scope.weaponPosition;
    sel.addEventListener("change", () => {
      scope.weaponPosition = { ...current(), preset: sel.value as WeaponPositionConfig["preset"] };
      this.changed();
    });
    combo.append(sel);
    preset.append(combo, el("span", "inherit", "per weapon"));
    wrap.append(preset);

    const grid = el("div", "xhair-fx-grid");
    for (const [label, key, min, max, step] of [
      ["X", "x", -100, 100, 1],
      ["Y", "y", -100, 100, 1],
      ["Z", "z", -100, 100, 1],
      ["Size", "size", 0.2, 3, 0.05],
    ] as [string, "x" | "y" | "z" | "size", number, number, number][]) {
      const field = el("label", "xhair-field");
      field.append(el("span", "", label));
      const input = el("input");
      input.type = "number";
      input.min = String(min);
      input.max = String(max);
      input.step = String(step);
      input.value = String(current()[key]);
      input.disabled = inherit && !scope.weaponPosition;
      dsElement(input, "input", { size: "sm", keep: "w-full" });
      input.addEventListener("input", () => {
        const v = Math.min(max, Math.max(min, Number(input.value) || 0));
        scope.weaponPosition = { ...current(), preset: current().preset, [key]: v };
        this.changed();
      });
      field.append(input);
      grid.append(field);
    }
    wrap.append(grid);
    return wrap;
  }
}
